import React, { useEffect, useState } from "react";

// Third party
import { Navigate, useLocation } from "react-router-dom";

// Hooks
import { useLogoutAPI } from '../hooks/API/welcome';
import { useAppDispatch, useAppSelector } from '../hooks';

// Components
import { decodeToken } from '../utils';
import { TProfileInfo, TShooterProfileInfo } from '../types';
import { profileDataActions, getAuthInfo, getProfileInfo } from '../store';
import { ESalesAccountType, ESuperUserAccountType, Role, UserType } from '../constants';

interface ProtectedRouteProps {
  allowedRoles?: (Role | null)[];
  allowedTypes: UserType[];
  allowedAccounts?: (ESuperUserAccountType | ESalesAccountType | null)[];
  children: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  allowedRoles,
  allowedTypes,
  allowedAccounts,
  children,
}) => {
  const dispatch = useAppDispatch();
  const { logout } = useLogoutAPI();
  const authInfo = useAppSelector(getAuthInfo);
  const user = useAppSelector<TProfileInfo | TShooterProfileInfo | null>(getProfileInfo);

  const location = useLocation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem("authToken");

      if (token) {
        try {
          const decodedUser = decodeToken(token);

          dispatch(
            profileDataActions.setAuthInfo({
              access_token: token,
              user_id: decodedUser.user_id,
              user_type: decodedUser.user_type,
              role: decodedUser.role,
              account_type: decodedUser.account_type,
              token_type: decodedUser.token_type,
            })
          );
        } catch (error) {
          console.error("Invalid or expired token:", error);
          localStorage.removeItem("authToken");
          dispatch(profileDataActions.resetProfileInfo());
        }
      }
      setLoading(false);
    };

    if (!user) {
      validateToken().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user, dispatch]);

  if (loading) return null;

  if (!user) {
    void logout();
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const userRole = authInfo.role ? authInfo.role.toUpperCase() : null;
  const userType = authInfo.user_type?.toUpperCase();
  const userAccountType = authInfo.account_type ? authInfo.account_type.toUpperCase() : null;

  const isAccountValid = userType === UserType.PERSONAL ? true : allowedAccounts?.includes(userAccountType as ESuperUserAccountType);
  const isRoleValid = userType === UserType.PERSONAL ? true : allowedRoles?.includes(userRole as Role | null);

  if (
    !allowedTypes.includes(userType as UserType) ||
    !isRoleValid ||
    !isAccountValid
  ) {
    void logout();
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export default ProtectedRoute;
